import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { ExpandLess, ExpandMore, Add } from '@mui/icons-material'
import AppsIcon from '@mui/icons-material/Apps'
import KeyIcon from '@mui/icons-material/Key'
import UserIcon from '@mui/icons-material/Person'
import VibrationIcon from '@mui/icons-material/Vibration'
import TableChartIcon from '@mui/icons-material/TableChart'

import {
    useTheme, Box, Collapse, IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material'

import { acDrawerToggle, acDrawerCollapse } from '../../../store/actions'
import { DRAWER_KEYS, ROUTES } from '../../../store/constants'

const DRAWER_SECTIONS = {
    [DRAWER_KEYS.USERS]: {
        id: DRAWER_KEYS.USERS,
        title: 'Usuários',
        string: 'usersManagement',
        icon: UserIcon,
        subSections: [],
        style: {}
    },
    [DRAWER_KEYS.APPLICATION_SERVICES]: {
        id: DRAWER_KEYS.APPLICATION_SERVICES,
        title: 'Aplicações',
        string: 'applicationServices',
        icon: AppsIcon,
        subSections: [],
        style: {}
    },
    [DRAWER_KEYS.ACCESS_KEYS]: {
        id: DRAWER_KEYS.ACCESS_KEYS,
        title: 'Chaves de Acesso',
        string: 'keyManager',
        icon: KeyIcon,
        subSections: [],
        style: {}
    },
    [DRAWER_KEYS.PLANS]: {
        id: DRAWER_KEYS.PLANS,
        title: 'Planos',
        string: 'plans',
        icon: VibrationIcon,
        subSections: [],
        style: {}
    },
    [DRAWER_KEYS.PLAN_GROUPS]: {
        id: DRAWER_KEYS.PLAN_GROUPS,
        title: 'Grupos de Planos',
        string: 'planGroups',
        icon: TableChartIcon,
        subSections: [],
        style: {}
    }
}

const DrawerContent = ({ selectedKey, temporary = false }) => {

    const theme = useTheme()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const darkMode = useSelector(state => state.auth.darkMode)
    const collapseOpen = useSelector(state => state.drawer.collapseOpen)
    const isDrawerOpen = useSelector(state => state.drawer.isOpen)

    const sections = Object.values(DRAWER_SECTIONS)
    const isExpanded = isDrawerOpen || temporary

    const _getColor = id => id === selectedKey ? theme.palette.primary.main : theme.palette.text.primary

    const handleRedirect = route => {
        navigate(route)
        if (temporary)
            dispatch(acDrawerToggle())
    }

    const handleCollapseToggle = (sectionString) => {
        if (isExpanded) {
            dispatch(acDrawerCollapse(sectionString))
        }
    }

    const handleAddClick = (section, event) => {
        event.stopPropagation()
        console.log(`Adicionar novo item em ${section.title}`)
        // Implementar lógica específica para cada seção
    }

    useEffect(() => {
        if (!isDrawerOpen && !temporary) {
            Object.keys(collapseOpen).forEach(key => {
                if (collapseOpen[key]) {
                    dispatch(acDrawerCollapse(key))
                }
            })
        }
    }, [isDrawerOpen, temporary, collapseOpen, dispatch])

    return (
        <List
            sx={{
                flexGrow: 1,
                width: isExpanded ? 'auto' : '60px',
                overflow: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                })
            }}
        >
            {sections.map((section) =>
                section.subSections.length > 0 ? (
                    <div key={section.string}>
                        <ListItem
                            disablePadding
                            sx={{
                                ...section.style,
                                justifyContent: isExpanded ? 'initial' : 'center',
                            }}
                        >
                            <ListItemButton
                                onClick={() => handleCollapseToggle(section.string)}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: isExpanded ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: _getColor(section.id),
                                        minWidth: 0,
                                        mr: isExpanded ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <section.icon />
                                </ListItemIcon>
                                {isExpanded && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <ListItemText
                                            primary={section.title}
                                            sx={{
                                                color: _getColor(section.id),
                                                opacity: isExpanded ? 1 : 0,
                                                flex: 1,
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(event) => handleAddClick(section, event)}
                                            sx={{
                                                color: _getColor(section.id),
                                                mr: 1,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                }
                                            }}
                                        >
                                            <Add fontSize="small" />
                                        </IconButton>
                                        {collapseOpen[section.string] ? <ExpandLess /> : <ExpandMore />}
                                    </Box>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Collapse
                            in={collapseOpen[section.string] && isExpanded}
                            sx={darkMode
                                ? { boxShadow: 'inset 0 0 3px rgb(255, 255, 255, 0.2)', backgroundColor: '#333' }
                                : { boxShadow: 'inset 0 0 3px rgb(0, 0, 0, 0.2)', backgroundColor: 'rgb(0, 0, 0, 0.02)' }
                            }
                        >
                            {
                                section.subSections.map(subSection => (
                                    <ListItem key={subSection.string} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleRedirect(ROUTES[subSection.id])}
                                            sx={{
                                                ...subSection.style,
                                                pl: 4,
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: _getColor(subSection.id) }}>
                                                <subSection.icon />
                                            </ListItemIcon>
                                            <ListItemText primary={subSection.title} sx={{ color: _getColor(subSection.id) }} />
                                        </ListItemButton>
                                    </ListItem>
                                ))
                            }
                        </Collapse>
                    </div>
                ) : (
                    <ListItem
                        key={section.string}
                        disablePadding
                        sx={{
                            ...section.style,
                            justifyContent: isExpanded ? 'initial' : 'center',
                        }}
                    >
                        <ListItemButton
                            onClick={() => handleRedirect(ROUTES[section.id])}
                            sx={{
                                minHeight: 48,
                                justifyContent: isExpanded ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    color: _getColor(section.id),
                                    minWidth: 0,
                                    mr: isExpanded ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <section.icon />
                            </ListItemIcon>
                            {isExpanded && (
                                <ListItemText
                                    primary={section.title}
                                    sx={{
                                        color: _getColor(section.id),
                                        opacity: isExpanded ? 1 : 0,
                                    }}
                                />
                            )}
                        </ListItemButton>
                    </ListItem>
                )
            )}
        </List>
    )
}

DrawerContent.propTypes = {
    selectedKey: PropTypes.string.isRequired,
    temporary: PropTypes.bool
}

export default DrawerContent